import React, { useEffect, useState } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { theme, Button, Modal } from 'antd';
import { useDispatch, useSelector } from 'react-redux';
import { ExclamationCircleOutlined } from '@ant-design/icons';
import { globalOp } from 'store/ducks/Global';
import { RootState } from 'store/configureStore';
import GoBackButton from 'components/GoBackButton';
import { hideDataEntry, hideWorkingAreas } from 'constants/hiddenAreas';
import ColorPalettes from 'constants/colorPalettes';
import CustomSelect from 'components/CustomSelect';
import { ThemeList } from 'store/ducks/globalTypes';
import { renderInstructionsContent } from 'constants/renderInstructionsContent';
import { ITitleBarProps } from 'store/ducks/Global/types';
import { useTranslation } from 'react-i18next';
import { getInstructionsModel } from 'constants/getInstructionsModel';
import i18n from 'i18n';
import { languageOptions } from 'constants/languages';
import * as Styled from './TitleBar.styled';

const { useToken } = theme;

const TitleBar: React.FC<ITitleBarProps> = ({ title }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { token } = useToken();
  const { t } = useTranslation();
  const items = getInstructionsModel(t);
  const location = useLocation();

  const [isEnglishModalVisible, setEnglishModalVisible] = useState(false);
  const [openThemeSidebar, setOpenThemeSidebar] = useState(false);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const colorPalettes = ColorPalettes(t);
  const toggleSidebar = () => setOpenThemeSidebar((prev) => !prev);
  const closeSidebar = () => setOpenThemeSidebar(false);
  const showEnglishModal = () => setEnglishModalVisible(true);
  const handleEnglishOk = () => setEnglishModalVisible(false);
  const handleEnglishCancel = () => setEnglishModalVisible(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 991);
  const { scheme, selectedOption99 } = useSelector(
    (state: RootState) => state.global,
  );

  const handleLanguageChange = (lang: string) => {
    i18n.changeLanguage(lang);
    localStorage.setItem('i18nextLng', lang);
  };

  const showModalForReports = () => {
    setIsModalVisible(true);
  };

  const handleOk = () => {
    setIsModalVisible(false);
  };

  const handleCancel = () => {
    setIsModalVisible(false);
  };

  const handleThemeChange = (value: ThemeList) => {
    localStorage.setItem('theme', value);
    dispatch(globalOp.setTheme(value) as any);
  };

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 991);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  const path = location.pathname;

  const handleLogOut = () => {
    Modal.confirm({
      title: t('WORKING_AREAS.SURE_TO_LOGOUT'),
      icon: <ExclamationCircleOutlined />,
      content: t('WORKING_AREAS.LOGOUT_INFO'),
      okText: t('WORKING_AREAS.CONFIRM'),
      cancelText: t('WORKING_AREAS.CANCEL'),
      onOk: async () => {
        try {
          const response = await globalOp.logout();
          localStorage.removeItem('authToken');
          localStorage.removeItem('role');
          localStorage.removeItem('roles');
          navigate('/');
        } catch (error) {
          localStorage.removeItem('authToken');
          localStorage.removeItem('role');
          localStorage.removeItem('roles');
          navigate('/');
        }
      },
    });
  };

  return (
    <>
      <Styled.StyledNav token={token}>
        {/* left */}
        <Styled.LeftZone>
          <Link to="/working_area">
            <Styled.StyledLogo src={Styled.logoSrc} alt="logo" />
          </Link>
        </Styled.LeftZone>

        {/* fnter */}
        <Styled.CenterZone>
          <Styled.AreaTitle {...({ isShort: title.length < 30 } as any)}>
            <Styled.TitleContainer theme={scheme}>
              <Styled.DataEntryTitle>{title}</Styled.DataEntryTitle>
            </Styled.TitleContainer>
          </Styled.AreaTitle>
        </Styled.CenterZone>

        {/* right */}
        <Styled.RightZone>
          <Styled.NavButtonsRow>
            {!hideWorkingAreas.includes(path) && (
              <GoBackButton
                to="/working_area"
                text={t('GENERAL.GO_TO_WORKING_AREAS')}
              />
            )}
            {!hideDataEntry.includes(path) && (
              <GoBackButton text={t('GENERAL.GO_BACK')} goBack />
            )}
          </Styled.NavButtonsRow>
          <Styled.DesktopButtonsColumn>
            <Button type="primary" onClick={showEnglishModal}>
              {t('WORKING_AREAS.INSTRUCTIONS')}
            </Button>

            <Button type="primary" onClick={toggleSidebar}>
              {t('WORKING_AREAS.SELECT_COLOR_THEME')}
            </Button>

            <Styled.ConsiderModalBtn
              type="primary"
              onClick={showModalForReports}
            >
              {t('WORKING_AREAS.AREA_SPECIFIC_INFORMATION')}
            </Styled.ConsiderModalBtn>
            <CustomSelect
              size="md"
              options={languageOptions}
              value={i18n.language}
              onChange={handleLanguageChange}
            />
          </Styled.DesktopButtonsColumn>

          <Styled.LogoutBtn onClick={handleLogOut} token={token}>
            {t('WORKING_AREAS.LOGOUT')}
          </Styled.LogoutBtn>
        </Styled.RightZone>
      </Styled.StyledNav>
      <Styled.MobileButtonsRow>
        <Button type="primary" onClick={showEnglishModal}>
          {t('WORKING_AREAS.INSTRUCTIONS')}
        </Button>

        <Button type="primary" onClick={toggleSidebar}>
          {t('WORKING_AREAS.SELECT_COLOR_THEME')}
        </Button>

        <Styled.ConsiderModalBtn type="primary" onClick={showModalForReports}>
          {t('WORKING_AREAS.AREA_SPECIFIC_INFORMATION')}
        </Styled.ConsiderModalBtn>
        <CustomSelect
          size="md"
          options={languageOptions}
          value={i18n.language}
          onChange={handleLanguageChange}
        />
      </Styled.MobileButtonsRow>
      <Styled.MobileNavButtonsRow>
        {!hideWorkingAreas.includes(path) && (
          <GoBackButton to="/working_area" text="Go to Working areas" />
        )}
      </Styled.MobileNavButtonsRow>
      {!hideDataEntry.includes(path) && (
        <Styled.MobileNavButtonsRow>
          <GoBackButton text="Go back" goBack />
        </Styled.MobileNavButtonsRow>
      )}
      <Modal
        title="Instructions"
        open={isEnglishModalVisible}
        onOk={handleEnglishOk}
        onCancel={handleEnglishCancel}
      >
        <ul>
          {items.map((item, index) => {
            if (item.type === 'text') {
              return (
                <li key={index}>
                  <strong>{item.label}:</strong> {item.value}
                </li>
              );
            }

            if (item.type === 'group') {
              return (
                <li key={index}>
                  <strong>{item.label}:</strong>
                  <ul>
                    {item.items.map((sub, i) => (
                      <li key={i}>{sub}</li>
                    ))}
                  </ul>
                </li>
              );
            }

            return null;
          })}
        </ul>
      </Modal>
      <Modal
        title="Please consider"
        open={isModalVisible}
        onOk={handleOk}
        onCancel={handleCancel}
      >
        {renderInstructionsContent(location.pathname, t)}
      </Modal>
      <Styled.Overlay show={openThemeSidebar} onClick={closeSidebar} />
      <Styled.Sidebar open={openThemeSidebar}>
        <h3> {t('WORKING_AREAS.SELECT_COLOR_THEME')}</h3>

        <CustomSelect
          size="lg"
          options={colorPalettes}
          value={scheme}
          onChange={(value: ThemeList) => {
            handleThemeChange(value);
            closeSidebar();
          }}
          filterOption={(input: string, option?: { label: string }) =>
            option?.label?.toLowerCase().includes(input.toLowerCase())
          }
        />
      </Styled.Sidebar>
    </>
  );
};

export default TitleBar;
